import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Countdown from 'react-countdown';

import ExemptSubMenu from '@/components/nav/ExemptSubMenu';
import ComingSoonPopup from '@/components/ui/ComingSoonPopup';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import { getLastDateOfTheMonth } from '@/utils/utils';

// import type { LayoutWrapperContents } from '@/types/LayoutWrapperContents';

const FormCA109 = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [removelistener, setRemovelistener] = useState(true);
  const [tempReturn, setTempReturn] = useState(false);
  // Define an array of imported images
  const images = [
    '/assets/images/2290-steps-img.webp',
    '/assets/images/easy-steps-img.webp',
    '/assets/images/941-steps-img.webp',
    // Add more image URLs as needed
  ];
  const Ref = React.useRef<HTMLDivElement>(null);

  // Handle scrolling to change images
  const handleScroll = (event: any) => {
    const delta = event.deltaY;
    if (delta > 0) {
      // Scroll down, show the next imag
      if (currentImageIndex < 2) {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
        setRemovelistener(false);
      } else {
        setRemovelistener(true);
      }
    } else if (delta < 0) {
      // Scroll up, show the previous image
      if (currentImageIndex > 0) {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
        setRemovelistener(false);
      } else {
        setRemovelistener(true);
      }
    } else {
      setRemovelistener(false);
    }
  };
  function useHorizontalScroll() {
    useEffect(() => {
      const el = Ref.current;
      setTempReturn(tempReturn);
      if (el) {
        const onWheel = (e: any) => {
          if (e.deltaY === 0) return;
          e.preventDefault();
          el.scrollTo({
            behavior: 'smooth',
            top: 0,
            left: 0,
          });
        };

        el.addEventListener('wheel', onWheel);
        if (removelistener === false) {
          el.addEventListener('wheel', onWheel);
        } else if (removelistener === true) {
          el.removeEventListener('wheel', onWheel);
        } else {
          el.addEventListener('wheel', onWheel);
        }
        return () => {
          el.removeEventListener('wheel', onWheel);
        };
      }
      return () => setTempReturn(false);
    }, [removelistener]);
    return Ref;
  }

  const scrollRef = useHorizontalScroll();

  const [open, setOpen] = useState(false);
  // const layoutWrapperContents: LayoutWrapperContents = {
  //   Heading: `Simplify your nonprofit's tax return with Form 990-EZ.`,
  //   SubHeading:
  //     'E-file now for a seamless experience. Stay tax-exempt and file confidently.',
  //   CtaText: 'E-file Form 990-EZ Now',
  //   CtaSubHeading: `Stay tax compliant with the IRS`,
  //   CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=sticky`,
  //   isActive: true,
  // };

  const [lastDayTaxSeason, setLastDayTaxSeason] = useState(new Date());

  useEffect(() => {
    setLastDayTaxSeason(getLastDateOfTheMonth(new Date().getFullYear(), 5, 15));
    setInterval(() => {
      setLastDayTaxSeason(
        getLastDateOfTheMonth(new Date().getFullYear(), 5, 15)
      );
    }, 1000); // Updates every second (adjust if needed)
  }, []);
  // Random component
  const Completionist = () => <span />;

  // Renderer callback with condition
  const renderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
  }: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) {
      // Render a completed state
      return <Completionist />;
    }
    // Render a countdown
    return (
      <Stack
        direction="row"
        justifyContent={{ xs: 'center', md: 'flex-start' }}
        alignItems="center"
        spacing={1}
        sx={{
          '& .timer-count': {
            border: '1px solid #848484',
            p: 1.5,
            textAlign: 'center',
            minWidth: { xs: 55, sm: 70, md: 70 },
            position: 'relative',
            borderRadius: 2,
            boxShadow: ' 0 0 20px rgba(0,0,0,.12)',
          },
          '& .timer-number': {
            fontSize: { xs: 18, sm: 24 },
            fontWeight: 900,
          },
          '& .colon': {
            fontSize: { xs: 18, sm: 24 },
            px: { xs: 0.2, sm: 0.5 },
            py: 0,
            mt: { xs: '-8px !important', sm: '-14px !important' },
          },
          '& .timer-label': {
            background: '#000',
            borderTop: '1px solid #848484',
            fontSize: { xs: 10, sm: 12 },
            fontWeight: 500,
            mt: 0,
            mx: -1.5,
            mb: -1.5,
            py: 0.2,
            px: { xs: 0.5, sm: 1, lg: 0.5 },
            borderRadius: '0px 0px 8px 8px',
            color: '#d7d7d7',
          },
        }}
      >
        <Box className="timer-count">
          <Box className="timer-number">
            {days?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Day</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {hours?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Hours</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {minutes?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Minutes</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {seconds?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Seconds</Box>
        </Box>
      </Stack>
    );
  };

  return (
    <AppLayout
      //  LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          title="E-File CA Form 109 | California UBI Tax Return"
          description="Have $1,000+ in gross UBI? E-file CA Form 109 online with TaxZerone. Prepare, review, and securely file your California UBI tax return with ease."
          keywords=""
          canonical="tax-exempt/efile-ftb-ca-form109-online/"
          jsonLdPricing="69.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="1062"
          ogShareImg="assets/images/exempt-share-img.png"
        />
      }
    >
      <ExemptSubMenu />
      {/* <Form199Popup /> */}
      <ComingSoonPopup open={open} setOpen={setOpen} />
      {/* <Container
        sx={{
          position: 'relative',
          mt: 2,
          mb: 0,
        }}
        maxWidth="lg"
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #3a0ca3, #4cc9f0 )',
            p: 1.5,
            px: 3,
            color: '#fff',
          }}
          className="ribbon"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Typography
              variant="body1"
              sx={{
                textAlign: { xs: 'center', sm: 'left' },
                fontSize: { xs: 16, sm: 17, md: 22, lg: '1.28rem' },
                color: '#dedede',
              }}
            >
              <b style={{ color: '#ffea00' }}>Attention Nonprofits! </b> File
              your{' '}
              <b style={{ color: '#ecf39e', fontWeight: 600 }}>
                {' '}
                2025 California Form 199 online{' '}
              </b>{' '}
              report annual information and stay compliant.
            </Typography>
            <Box> */}
      {/* <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mb: { xs: 1, sm: 1 },
                  lineHeight: 1.3,
                }}
              >
                Time Left Until the Deadline
                <span style={{ fontWeight: 600, color: '#fff' }}>
                  ( May 15, 2026)
                </span>
                :
              </Typography> */}
      <Countdown date={lastDayTaxSeason} renderer={renderer} />
      {/* </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=ca199`}
                sx={{
                  backgroundColor: '#000',
                  '&:hover, &:focus': {
                    backgroundColor: '#213e99',
                    color: '#fff',
                    boxShadow: 'none',
                  },
                  px: 2,
                  color: '#fff',
                  mt: { xs: 0, md: 0 },
                  display: { xs: 'inline-block', sm: 'inline-block' },
                  mx: { xs: 'auto', sm: 1 },
                  borderRadius: 3,
                  boxShadow: 'none',
                  fontSize: { xs: 14, sm: 16, md: 18 },
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                E-File Now!
              </Button> */}
      {/* <Typography
                variant="body2"
                sx={{ textAlign: 'center', mt: 1, color: '#333' }}
              >
                {' '}
                <b style={{ color: '#333' }}> Save 10%</b> with code{' '}
                <b style={{ color: '#c02652' }}> TZOTE10</b>
              </Typography> */}
      {/* </Box>
          </Stack>
        </Box>
      </Container> */}
      <Container maxWidth="xl" component="section">
        <Hidden mdUp smDown>
          <Box
            sx={{
              mb: -1.5,
              textAlign: 'center',
              mt: 0,
              position: 'relative',
            }}
          >
            <Box
              component="picture"
              sx={{
                position: { xs: 'relative', sm: 'absolute' },
                right: 20,
                top: { xs: 0, sm: 20 },
              }}
            >
              <source
                srcSet="/assets/images/ftb-logo.webp"
                className="mw-100"
                width={60}
                height={60}
              />
              <source
                srcSet="/assets/images/ftb-logo.png"
                className="mw-100"
                width={60}
                height={60}
              />
              <img
                src="/assets/images/ftb-logo.png"
                loading="lazy"
                alt="FTB e-file provider"
                className="mw-100"
                width={60}
                height={60}
              />
            </Box>
          </Box>
        </Hidden>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                E-File California{' '}
                <span className="highlight-container">
                  <span className="highlight">Form 109</span>
                </span>{' '}
                for Unrelated Business Income
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Have more than $1,000 in gross unrelated business income (UBI)?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Form 109 is the California tax return used to report and pay tax
                on that income. Prepare, review, and securely e-file your return
                online with TaxZerone.
              </Typography>

              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box sx={{ textAlign: 'center', mr: { xs: 0, md: 4 } }}>
                  <Button
                    variant="contained"
                    className="btn-primary"
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=californiaform109`}
                  >
                    Start E-Filing Form 109
                  </Button>
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      mb: { xs: '0 !important', md: '2px !important' },
                    }}
                  >
                    <picture>
                      <source
                        srcSet="/assets/images/ftb-logo.webp"
                        //  className="mw-100"
                        width={90}
                        height={90}
                      />
                      <source
                        srcSet="/assets/images/ftb-logo.png"
                        //  className="mw-100"
                        width={90}
                        height={90}
                      />
                      <img
                        src="/assets/images/ftb-logo.png"
                        loading="lazy"
                        alt="FTB e-file provider"
                        //  className="mw-100"
                        width={90}
                        height={90}
                      />
                    </picture>
                  </Box>
                  <Box
                    sx={{
                      mb: { xs: '0px !important', md: '2px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/tzo-soc2.webp" />
                      <source srcSet="/assets/images/tzo-soc2.png" />
                      <Box
                        component="img"
                        src="/assets/images/tzo-soc2.png"
                        loading="lazy"
                        alt="FTB e-file provider"
                        //  className="mw-100"
                        width={90}
                        height={90}
                        sx={{
                          width: { xs: 70, md: 90 },
                          height: { xs: 69, md: 90 },
                        }}
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>
              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={2}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{
                  mt: '20px !important',
                  mb: { xs: '116px !important', sm: '16px !important' },
                }}
              >
                <Grid container spacing={1} sx={{ mt: 0, mb: 4 }}>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Stack
                      direction={{ xs: 'row', sm: 'row' }}
                      spacing={1}
                      alignItems="center"
                      justifyContent={{
                        xs: 'flex-start',
                        sm: 'center',
                        md: 'flex-start',
                      }}
                    >
                      <img
                        src="/assets/images/ftb-logo.png"
                        alt="IRS-Authorized"
                        className="banner-icon"
                        width={32}
                        height={32}
                      />
                      <Typography
                        sx={{
                          color: '#14181F',
                          fontSize: { xs: 15, sm: 13, md: 14, lg: 15 },
                          fontWeight: 500,
                        }}
                      >
                        FTB - Authorized
                      </Typography>
                    </Stack>{' '}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent={{
                        xs: 'flex-start',
                        sm: 'center',
                        md: 'flex-start',
                      }}
                    >
                      <img
                        src="/assets/images/rate-icon.png"
                        alt="99% Acceptance rate"
                        className="banner-icon"
                        width={32}
                        height={32}
                      />
                      <Typography
                        sx={{
                          color: '#14181F',
                          fontSize: { xs: 15, sm: 13, md: 14, lg: 15 },
                          fontWeight: 500,
                        }}
                      >
                        99% Acceptance rate
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Stack
                      direction={{ xs: 'row', sm: 'row' }}
                      spacing={1}
                      alignItems="center"
                      justifyContent={{
                        xs: 'flex-start',
                        sm: 'center',
                        md: 'flex-start',
                      }}
                    >
                      <img
                        src="/assets/images/secure-new-icon.png"
                        alt="Completely secure"
                        className="banner-icon"
                        width={32}
                        height={32}
                      />
                      <Typography
                        sx={{
                          color: '#14181F',
                          fontSize: { xs: 15, sm: 13, md: 14, lg: 15 },
                          fontWeight: 500,
                        }}
                      >
                        Completely secure
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent={{
                        xs: 'flex-start',
                        sm: 'center',
                        md: 'flex-start',
                      }}
                    >
                      <img
                        src="/assets/images/instant-icon-new.png"
                        alt="Receive instant update"
                        className="banner-icon"
                        width={32}
                        height={32}
                      />
                      <Typography
                        sx={{
                          color: '#14181F',
                          fontSize: { xs: 15, sm: 13, md: 14, lg: 15 },
                          fontWeight: 500,
                        }}
                      >
                        Receive instant update
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          </Box>
          <Hidden mdDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/caf109-landing-img.webp"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <source
                  srcSet="/assets/images/caf109-landing-img.png"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <img
                  src="/assets/images/caf109-landing-img.png"
                  alt="E-File California Form 109 for Unrelated Business Income"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden mdUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/caf109-landing-img-sm.webp"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <source
                  srcSet="/assets/images/caf109-landing-img-sm.png"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <img
                  src="/assets/images/caf109-landing-img-sm.png"
                  alt="E-File California Form 109 for Unrelated Business Income"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Container>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
          className="gradient-bg"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 30, sm: 26, xs: 22 },
                  fontWeight: 500,
                  mb: 0,
                  textAlign: 'left',
                  lineHeight: 1.5,
                }}
              >
                File California Form 109 online for just{' '}
                <Box
                  component="strong"
                  sx={{
                    color: '#ffe400',
                    fontSize: { xs: 30, sm: 36, md: 48, lg: 56 },
                  }}
                >
                  $69.99
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 16, md: 16, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#f3f3f3',
                  mb: 0,
                  textAlign: 'left',
                }}
              >
                TaxZerone delivers a secure, affordable, and hassle-free way to
                prepare and e-file FTB Form 109 online.
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100', sm: 250, md: 280 },
                textAlign: 'center',
                fontSize: 18,
                lineHeight: 1.2,
              }}
            >
              <Button
                size="large"
                variant="contained"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=californiaform109`}
                sx={{
                  boxShadow: 'none',
                  borderRadius: 2,
                  mb: 0.5,
                  textTransform: 'none',
                  backgroundColor: '#fff',
                  color: '#0a7581',
                  '&:hover, &:focus, &:active': {
                    color: '#fff',
                    backgroundColor: '#172b6b',
                  },
                }}
              >
                E-File Now
              </Button>
              {/* <Typography
                variant="body2"
                sx={{ mt: 0, mb: 0, fontSize: { xs: 11, sm: 14 } }}
              >
                No additional or hidden cost for supporting schedules.
              </Typography> */}
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#ffffff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <picture>
                  <source srcSet="/assets/images/california-form-109-img.webp" />
                  <source srcSet="/assets/images/california-form-109-img.png" />
                  <img
                    src="/assets/images/california-form-109-img.png"
                    alt="What Is California Form 109?"
                    className="img-responsive-boir-img"
                    loading="lazy"
                    width={374}
                    height={500}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,
                  textAlign: 'left',
                }}
              >
                What Is California Form 109?
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                CA Form 109 (Exempt Organization Business Income Tax Return) is
                a California Franchise Tax Board (FTB) tax return used by
                eligible tax-exempt organizations to report unrelated business
                income (UBI) and calculate any California tax due on that
                income. 
              </Typography>
              <Box
                component="ul"
                sx={{
                  mb: 2,
                  '& li': { mb: 1.2 },
                }}
              >
                <li>
                  <strong style={{ fontWeight: 600 }}>
                    Official Purpose –
                  </strong>{' '}
                  Reports UBI and calculates the tax on that income under
                  applicable California FTB rules.
                </li>
                <li>
                  <strong style={{ fontWeight: 600 }}>Filed With –</strong>{' '}
                  {/* <Link
                    href="https://www.ftb.ca.gov/forms/2025/2025-109.pdf"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  > */}{' '}
                  California Franchise Tax Board
                  {/* </Link>{' '} */}
                  (FTB).
                </li>
                <li>
                  <strong style={{ fontWeight: 600 }}>Income Reported –</strong>{' '}
                  Business income from activities that are not substantially
                  related to an organization's tax-exempt purpose.
                </li>
                <li>
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Tax Calculation –
                  </strong>{' '}
                  Calculates any applicable California tax on unrelated business
                  income.
                </li>
                <li>
                  <strong style={{ fontWeight: 600 }}> Return Type –</strong> A
                  California state business{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    income tax return
                  </strong>{' '}
                  for qualifying exempt organizations and certain nonexempt
                  charitable trusts.
                </li>
              </Box>
              <Alert
                severity="warning"
                icon={false}
                sx={{
                  mb: 4,
                  textAlign: 'left',
                  borderLeft: '3px solid rgb(102, 60, 0)',
                }}
              >
                <span style={{ fontWeight: 600 }}>Note:</span>

                <i>
                  {' '}
                  FTB CA Form 109 is separate from your organization's annual
                  California informational return. If your organization has a
                  <Link
                    href="/tax-exempt/efile-ftb-ca-form199-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 199
                  </Link>{' '}
                  or <span style={{ fontWeight: 600 }}>Form 199N</span> filing
                  requirement, that return is filed separately.
                </i>
              </Alert>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
              <Box sx={{ maxWidth: 800, textAlign: 'center', mx: 'auto' }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                    textAlign: 'center',
                  }}
                >
                  What Is Unrelated Business Income (UBI){' '}
                  <Hidden smDown>
                    {' '}
                    <br />
                  </Hidden>{' '}
                  and How Is UBTI Calculated?
                </Typography>

                <Typography variant="body1">
                  Form 109 for CA is based on the concept of{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Unrelated Business Taxable Income (UBTI),
                  </strong>{' '}
                  which is derived from your{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Unrelated Business Income (UBI)
                  </strong>{' '}
                  by deducting allowable deductions. This will help you to know
                  when you have to file Form 109.
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  p: 3,
                  minHeight: '100%',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 17, xs: 16 },
                    mb: 1,
                  }}
                >
                  UBI
                </Typography>
                <Typography variant="body1" sx={{ textAlign: 'left' }}>
                  Income earned from a{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    trade or business
                  </strong>{' '}
                  that is{' '}
                  <strong style={{ fontWeight: 600 }}>
                    regularly carried on
                  </strong>{' '}
                  and{' '}
                  <strong style={{ fontWeight: 600 }}>
                    not substantially related
                  </strong>{' '}
                  to your organization's exempt purpose.
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, '& li': { mb: 1.2, textAlign: 'left' } }}
                >
                  <li>Subject to taxation in California.</li>
                  <li>Serves as the basis for computing the UBTI.</li>
                  <li>
                    UBI gross greater than $1,000 can give rise to a Form 109
                    reporting requirement.
                  </li>
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{ borderRadius: 3, p: 3, py: 3.0, minHeight: '100%' }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 17, xs: 16 },
                    mb: 1,
                    textAlign: 'center',
                  }}
                >
                  UBTI
                </Typography>
                <Typography variant="body1">
                  The taxable amount calculated from{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    UBI after allowable deductions.
                  </strong>{' '}
                  Form 109 uses UBTI to determine any tax due.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Subject to taxation in California.</li>
                  <li>Serves as the basis for computing the UBTI.</li>
                  <li>
                    UBI gross greater than $1,000 can give rise to a Form 109
                    reporting requirement.
                  </li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 1,
                  textAlign: 'left',
                }}
              >
                When Does Your Organization Need to File California Form 109?
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your organization may need to{' '}
                <strong style={{ fontWeight: 600 }}> file CA Form 109</strong>{' '}
                if it has{' '}
                <strong style={{ fontWeight: 600 }}> more than $1,000</strong>{' '}
                in gross unrelated business income (
                <strong style={{ fontWeight: 600 }}>UBI</strong>) from
                activities conducted in California. The return is generally
                required for tax-exempt organizations and certain nonexempt
                charitable trusts with California unrelated business taxable
                income (UBTI).
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { md: 20, sm: 18, xs: 16 },
                  mb: 0.8,
                  fontWeight: 600,
                }}
              >
                Key Filing Requirements
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyleType: 'none',
                  paddingLeft: 0,
                  textAlign: 'left',
                  ml: { xs: 0, sm: 5 },
                  mt: { xs: 0, md: 4 },
                  mb: { xs: 0, md: 3 },
                  '& li': { display: 'flex', mb: 2, ml: -2 },
                }}
              >
                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>
                    <span style={{ fontWeight: 600 }}>
                      Gross UBI Requirement – More than $1,000
                    </span>{' '}
                    in gross unrelated business income (UBI) during the taxable
                    year.
                  </Box>
                </li>

                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Eligible Organizations -
                    </span>{' '}
                    Organizations such as{' '}
                    <Link
                      href="/resources/tax-exempt/501c3-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      501(c)(3)
                    </Link>{' '}
                    public charities, private foundations, trade and
                    professional associations, employee trusts, and other
                    tax-exempt organizations fall in this category.
                  </Box>
                </li>
                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Non-exempt Charitable Trusts –
                    </span>{' '}
                    Some charitable trusts as per IRC{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Section 4947(a)(1)
                    </span>{' '}
                    may require filing as well.
                  </Box>
                </li>
                <li>
                  <CheckCircleIcon sx={{ color: '#1da94a', mr: 1 }} />
                  <Box>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Multi-state Operations –
                    </span>{' '}
                    Organizations engaged in unrelated business activities both
                    within and outside California need to apportion their
                    income.
                  </Box>
                </li>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: '#f3f3f3',
                  borderRadius: '50%',
                }}
              >
                <picture>
                  <source srcSet="/assets/images/organization-need-caf109-img.webp" />
                  <source srcSet="/assets/images/organization-need-caf109-img.png" />
                  <img
                    src="/assets/images/organization-need-caf109-img.png"
                    alt="When Does Your Organization Need to File California Form 109?"
                    className="img-responsive-boir-img"
                    // style={{
                    //   filter: 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.25))',
                    // }}
                    loading="lazy"
                    width={500}
                    height={500}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: 5,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
          >
            <Grid
              size={12}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Box
              // sx={{
              //   textAlign: 'center',
              //   alignItems: 'center',
              //   display: 'flex',
              //   justifyContent: 'center',
              // }}
              >
                {' '}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 1,
                    mt: 2,
                  }}
                >
                  Should Your Organization File California Form 109 or Form 199?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 0,
                  }}
                >
                  <strong style={{ fontWeight: 600 }}> Form 109</strong> and{' '}
                  <strong style={{ fontWeight: 600 }}>Form 199</strong> serve
                  different filing purposes. Form 109 is used to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    report and pay tax
                  </strong>{' '}
                  on unrelated business taxable income (UBTI), while Form 199 is
                  the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    annual information return
                  </strong>{' '}
                  for California tax-exempt organizations. Depending on your
                  organization's activities and filing requirements, you may
                  need to file one or both returns.
                </Typography>
              </Box>
            </Grid>
            <Grid size={12}>
              <Box component="div" sx={{ mb: 0 }}>
                <TableContainer
                  sx={{ border: '1px solid #828282', borderRadius: 2 }}
                >
                  <Table aria-label="California Filing Requirements">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#e9e9e9' }}>
                        <TableCell
                          sx={{
                            color: '#444',
                            width: 100,
                            borderRight: '1px solid #828282',
                            textAlign: 'left',
                            fontWeight: 600,
                            fontSize: 16,
                          }}
                        >
                          Features
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#444',
                            width: 200,
                            borderRight: '1px solid #828282',
                            textAlign: 'left',
                            fontWeight: 600,
                            fontSize: 16,
                            backgroundColor: '#fff',
                          }}
                        >
                          CA Form 109 - TAX RETURN
                        </TableCell>

                        <TableCell
                          sx={{
                            color: '#444',
                            textAlign: 'left',
                            width: 200,
                            fontSize: 16,
                            fontWeight: 600,
                          }}
                        >
                          CA Form 199 - INFORMATION RETURN
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow>
                        <TableCell
                          sx={{
                            textAlign: 'Left',
                            borderRight: '1px solid #828282',
                            fontWeight: 600,
                            fontSize: 16,
                          }}
                        >
                          Filing Trigger
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'Left',
                            borderRight: '1px solid #828282',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            {' '}
                            More than $1,000 in gross UBI,
                          </strong>{' '}
                          subject to applicable rules
                        </TableCell>

                        <TableCell sx={{ textAlign: 'left', fontSize: 16 }}>
                          Applicable California exempt organization
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                        <TableCell
                          sx={{
                            textAlign: 'Left',
                            borderRight: '1px solid #828282',
                            fontWeight: 600,
                            fontSize: 16,
                          }}
                        >
                          Tax Payment
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'Left',
                            borderRight: '1px solid #828282',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          May require payment
                        </TableCell>

                        <TableCell sx={{ textAlign: 'left', fontSize: 16 }}>
                          Informational return only
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{
                            textAlign: 'Left',
                            borderRight: '1px solid #828282',
                            fontWeight: 600,
                            fontSize: 16,
                          }}
                        >
                          Taxable Income
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'Left',
                            borderRight: '1px solid #828282',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          Reports UBTI
                        </TableCell>
                        <TableCell sx={{ textAlign: 'left', fontSize: 16 }}>
                          Does not report UBTI
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                        <TableCell
                          sx={{
                            textAlign: 'Left',
                            borderRight: '1px solid #828282',
                            fontWeight: 600,
                            fontSize: 16,
                          }}
                        >
                          Federal Equivalent
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'Left',
                            borderRight: '1px solid #828282',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          <Link
                            href="/tax-exempt/e-file-irs-form-990-t/"
                            sx={{ color: '#0202ff' }}
                          >
                            {' '}
                            IRS Form 990-T
                          </Link>
                        </TableCell>

                        <TableCell sx={{ textAlign: 'left', fontSize: 16 }}>
                          <Link
                            href="/tax-exempt/e-file-irs-form-990/"
                            sx={{ color: '#0202ff' }}
                          >
                            IRS Form 990,
                          </Link>{' '}
                          <Link
                            href="/tax-exempt/e-file-irs-form-990-ez/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 990-EZ
                          </Link>
                          , or{' '}
                          <Link
                            href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=californiaform109`}
                            sx={{ color: '#0202ff' }}
                          >
                            Form 990-N
                          </Link>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Alert
                severity="warning"
                icon={false}
                sx={{ borderLeft: '3px solid rgb(102, 60, 0)', mt: 4, mb: 4 }}
              >
                <b>Note:</b> The{' '}
                <Link
                  href="/resources/tax-exempt/form-109-instructions/"
                  sx={{ color: '#0202ff' }}
                >
                  CA 109 Form
                </Link>{' '}
                does not replace California Form 199 or Form 199N. If your
                organization meets the filing requirements for both returns,
                each must be filed separately with the{' '}
                <strong style={{ fontWeight: 600 }}>
                  {' '}
                  California Franchise Tax Board (FTB)
                </strong>
                .
              </Alert>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ my: { xs: 2, sm: 6, md: 6 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center" />
          </Grid>
        </Container>

        <Container
          maxWidth="xl"
          sx={{ mb: 5 }}
          ref={scrollRef}
          className="image-gallery"
          onWheel={handleScroll}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: -3, md: -5, lg: -8 },
              }}
            >
              <picture>
                <source srcSet="/assets/images/caf109-steps-img.webp" />
                <source srcSet="/assets/images/caf109-steps-img.png" />
                <img
                  src="/assets/images/caf109-steps-img.png"
                  alt="What Information Do You Need to File Form 109?"
                  className="img-responsive"
                  loading="lazy"
                  width={915}
                  height={790}
                />
              </picture>
              {/* <img
                src={images[currentImageIndex]}
                alt={`Image ${currentImageIndex + 1}`}
                className="imagescroll"
                loading="lazy"
              /> */}
            </Box>

            <Box
              sx={{
                width: '100%',

                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 1,
                  textAlign: 'left',
                  lineHeight: 1.5,
                }}
              >
                What Information Do You Need to File Form 109?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                Before filing{' '}
                <Link
                  href="/resources/tax-exempt/form-109-instructions/"
                  sx={{ color: '#0202ff' }}
                >
                  California Form 109
                </Link>
                , gather your organization's financial, tax, and unrelated
                business income (UBI) information.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                Having these details ready helps you complete the return
                accurately and efficiently.
              </Typography>
              <Typography
                component="div"
                variant="body1"
                sx={{ mb: 3, color: '#6e6e6e' }}
              >
                <Box
                  component="ul"
                  sx={{
                    listStyleType: 'none',
                    paddingLeft: 0,
                    textAlign: 'left',
                    ml: { xs: 0, sm: 0 },
                    '& li': { display: 'flex' },
                  }}
                >
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>Organization Information </Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>Unrelated Business Income (UBI) Details </Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>Allowable Deductions</Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>California Apportionment Details</Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>Supporting Forms & Schedules </Box>
                  </li>
                </Box>
              </Typography>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 4,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=californiaform109`}
                >
                  Start E-Filing Now
                </Button>
                {/* <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: { xs: 2, sm: 2, md: 5 } }}
                >
                  Stay tax compliant with the IRS
                </Typography> */}
              </Box>
            </Box>
          </Stack>
        </Container>
      </Grid>

      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: 5,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 5 }, mt: { xs: -3, md: 5 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 32, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Supporting Forms & Schedules for CA Form 109
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 2,
                  }}
                >
                  Depending on your organization's activities, |
                  <strong style={{ fontWeight: 600 }}> Form 109</strong> may
                  <strong style={{ fontWeight: 600 }}>
                    require additional forms and schedules.
                  </strong>{' '}
                  <Link href="/" sx={{ color: '#0202ff' }}>
                    {' '}
                    TaxZerone
                  </Link>{' '}
                  supports the key California forms commonly associated with
                  Form 109 filing.
                </Typography>

                <Typography component="div" variant="body1" sx={{ mb: 3 }}>
                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      paddingLeft: 0,
                      textAlign: 'left',
                      ml: { xs: 0, sm: 3 },
                      '& li': { display: 'flex' },
                    }}
                  >
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/ftb-form-3885f-depreciation/"
                            sx={{ color: '#0202ff' }}
                          >
                            FTB Form 3885F
                          </Link>
                        </b>
                        &nbsp; - Report California depreciation and amortization
                        adjustments for exempt organizations and trusts.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>Schedule D (541)</b>
                        &nbsp;- Reports capital gains and losses for applicable
                        trusts.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>Schedule P (100)</b>
                        &nbsp;- Calculates alternative minimum tax (AMT) and
                        credit limitations for applicable organizations.
                      </Box>
                    </li>
                  </Box>
                </Typography>
                <Box>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Looking for additional guidance?
                    </strong>{' '}
                    Explore our{' '}
                    <Link
                      href="/resources/tax-exempt/form-109-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      FTB CA Form 109 instructions
                    </Link>{' '}
                    and resources to learn about filing requirements,
                    eligibility, deadlines, extensions, and step-by-step filing
                    guidance.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  my: { xs: 3, md: 0 },
                }}
              >
                <picture>
                  <source srcSet="/assets/images/support-caf109-img.webp" />
                  <source srcSet="/assets/images/support-caf109-img.png" />
                  <img
                    src="/assets/images/support-caf109-img.png"
                    alt="Supporting Form ca 199"
                    className="img-responsive"
                    loading="lazy"
                    width={550}
                    height={500}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          py: 5,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mt: 5, mb: 5, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 30, sm: 26, xs: 22 },
              fontWeight: 600,
              mb: 3,
            }}
          >
            When Is California Form 109 Due?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
              fontWeight: 400,
              // color: '#333333',
              mb: 2,
            }}
          >
            Know your filing deadline based on your organization's type and stay
            compliant with California FTB requirements.
          </Typography>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Grid size={{ md: 3, sm: 6, xs: 12 }}>
              <Box
                component="ul"
                sx={{
                  listStyleType: 'none',
                  paddingLeft: 0,
                  textAlign: 'left',
                  ml: { xs: 0, sm: 3 },
                  mt: { xs: 0, md: 3 },
                  mb: { xs: 0, md: 3 },
                  '& li': { display: 'flex', color: '#5f5f5f' },
                }}
              >
                {' '}
                <li
                  style={{
                    marginBottom: 10,
                    // color: '#b0b0b0',
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box> Public Charities (501(c)(3))</Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>Private Foundations </Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box> Trade Associations </Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                    // color: '#b0b0b0',
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box> Employee Trusts (except pension trusts) </Box>
                </li>
                <li
                  style={{
                    marginBottom: 0,
                    // color: '#b0b0b0',
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box> Other Exempt Organizations </Box>
                </li>
              </Box>
            </Grid>
            <Grid size={{ md: 3, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  p: 3,
                  py: 3,
                  mt: { xs: 1, sm: 0 },
                  textAlign: 'center',
                  // minHeight: '100%',
                  backgroundColor: '#e42527',
                  color: '#fff',
                  position: 'relative',
                  '&:before': {
                    display: { xs: 'none', sm: 'block' },
                    content: "''",
                    position: 'absolute',
                    top: '40%',
                    left: -32,
                    border: '16px solid transparent',
                    borderRightColor: '#e42527',
                  },
                  '&:after': {
                    display: { xs: 'block', sm: 'none' },
                    content: "''",
                    position: 'absolute',
                    left: 30,
                    top: -32,
                    border: '16px solid transparent',
                    borderBottomColor: '#e42527',
                  },
                }}
              >
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Return due date
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 0.5,
                    fontSize: { md: 38, sm: 30, xs: 26 },
                    fontWeight: 700,
                  }}
                >
                  May 15
                </Typography>
              </Paper>
            </Grid>
            <Hidden smDown>
              <Grid size={{ md: 3, sm: 6, xs: 12 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    p: 2,
                    py: 3,
                    textAlign: 'center',
                    // minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                    border: 'none',
                    position: 'relative',
                    '&:before': {
                      content: "''",
                      position: 'absolute',
                      top: '40%',
                      right: -32,
                      border: '16px solid transparent',
                      borderLeftColor: '#f0f2f9',
                    },
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Return due date
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      mb: 0.5,
                      fontSize: { md: 38, sm: 30, xs: 26 },
                      fontWeight: 700,
                      color: '#e42527',
                    }}
                  >
                    April 15
                  </Typography>
                </Paper>
              </Grid>
            </Hidden>
            <Grid size={{ md: 3, sm: 6, xs: 12 }}>
              <Hidden smUp>
                <Divider
                  sx={{
                    width: '100%',
                    height: '2px',
                    background: '#000000',
                    borderBottom: '1px solid #494949',
                    opacity: 1,
                    mt: 1,
                    mb: 3,
                  }}
                />
              </Hidden>
              <Box
                component="ul"
                sx={{
                  listStyleType: 'none',
                  paddingLeft: 0,
                  textAlign: 'left',
                  ml: { xs: 0, sm: 3 },
                  mt: { xs: 0, md: 3 },
                  mb: { xs: 0, md: 3 },
                  '& li': { display: 'flex', color: '#5f5f5f' },
                }}
              >
                <li
                  style={{
                    marginBottom: 10,
                    // color: '#b0b0b0',
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>Pension Trusts (IRC Section 401(a))</Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                    // color: '#b0b0b0',
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>Individual Retirement Arrangements (IRAs)</Box>
                </li>
              </Box>
            </Grid>
            <Hidden smUp>
              <Grid size={{ md: 3, sm: 6, xs: 12 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    p: 2,
                    py: 3,
                    textAlign: 'center',
                    // minHeight: '100%',
                    backgroundColor: '#fff',
                    position: 'relative',
                    '&:before': {
                      content: "''",
                      position: 'absolute',
                      left: 30,
                      top: -32,
                      border: '16px solid transparent',
                      borderBottomColor: '#fff',
                    },
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Return due date
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      mb: 0.5,
                      fontSize: { md: 38, sm: 30, xs: 26 },
                      fontWeight: 700,
                      color: '#e42527',
                    }}
                  >
                    April 15
                  </Typography>
                </Paper>
              </Grid>
            </Hidden>
            <Grid size={{ xs: 12 }}>
              <Divider
                sx={{
                  width: '100%',
                  height: '2px',
                  background: '#d2d2d2',
                  borderBottom: '1px solid #ededed',
                  opacity: 1,
                  my: 1,
                }}
              />
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 4,
                  p: 3,
                  backgroundColor: '#f0f2f9',
                  minHeight: '100%',
                  // display: 'grid',
                  // gridTemplateColumns: 'auto auto ',
                  columnGap: 1,
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  mx: 2,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/calender-icon.webp" />
                    <source srcSet="/assets/images/calender-icon.png" />
                    <img
                      src="/assets/images/calender-icon.png"
                      alt="Filing Deadline"
                      loading="lazy"
                      width={50}
                      height={50}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    my: 2,
                  }}
                >
                  Filing Deadline
                </Typography>
                <Typography variant="body1" sx={{ textAlign: 'center', mb: 0 }}>
                  Most exempt organizations must file FTB Form 109 by the 15th
                  day of the 5th month after the close of their taxable year.
                  Pension trusts and IRAs generally file by the 15th day of the
                  4th month.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 4,
                  p: 3,
                  backgroundColor: '#f0f2f9',
                  minHeight: '100%',
                  // display: 'grid',
                  // gridTemplateColumns: 'auto auto ',
                  columnGap: 1,
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  mx: 2,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/extension-available-icon.webp" />
                    <source srcSet="/assets/images/extension-available-icon.png" />
                    <img
                      src="/assets/images/extension-available-icon.png"
                      alt="Extension Available"
                      loading="lazy"
                      width={50}
                      height={50}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    my: 2,
                  }}
                >
                  Extension Available
                </Typography>
                <Typography variant="body1" sx={{ textAlign: 'center', mb: 0 }}>
                  An automatic 6-month extension is available if your
                  organization is in good standing. If tax is due, pay 100% of
                  the tax by the original due date using FTB Form 3539 to avoid
                  late payment penalties.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 4,
                  p: 3,
                  backgroundColor: '#f0f2f9',
                  minHeight: '100%',
                  // display: 'grid',
                  // gridTemplateColumns: 'auto auto ',
                  columnGap: 1,
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  mx: 2,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/late-filing-icon.webp" />
                    <source srcSet="/assets/images/late-filing-icon.png" />
                    <img
                      src="/assets/images/late-filing-icon.png"
                      alt="Late Filing & Payment"
                      loading="lazy"
                      width={50}
                      height={50}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    my: 2,
                  }}
                >
                  Late Filing & Payment
                </Typography>
                <Typography variant="body1" sx={{ textAlign: 'center', mb: 0 }}>
                  File and pay by the original due date to avoid penalties and
                  interest. Late-filing and late-payment penalties may apply,
                  with the combined penalties not exceeding 25% of the unpaid
                  tax.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          <Button
            variant="contained"
            sx={{
              mt: 4,
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            className="btn-primary medium"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=californiaform109`}
          >
            E-File Form 109
          </Button>
        </Container>
      </Grid>

      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Why Choose TaxZerone?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Prepare, review, and securely e-file California Form 109 with
                features specifically <br />
                created to streamline your state tax filing for nonprofits.
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} m={2} mt={4} pb={{ xs: 0, sm: 4, md: 7 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#ffff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/ftb-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/ftb-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/ftb-icon.png"
                      alt="FTB-Authorized E-Filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  FTB-Authorized E-Filing
                </Typography>
                <Typography variant="body1">
                  TaxZerone is a certified FTB e-filer, which means that you get
                  a secure and trusted portal to file your FTB Form 109.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#ffff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/free-efile-retransmission-icon.png"
                      alt="Free Retransmission"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Free Retransmission
                </Typography>
                <Typography variant="body1">
                  In case your FTB Form 109 is rejected due to any correctable
                  problem, you can amend your form and send it again free of
                  charge.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#ffff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/support-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/support-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/support-icon.png"
                      alt="24/7 Customer Support"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  24/7 Customer Support
                </Typography>
                <Typography variant="body1">
                  Live chat and email customer support available to help you
                  with any questions related to filing or technical problems
                  with your account. 
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#ffff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/guided-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/guided-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/guided-icon.png"
                      alt="Guided Filing Experience"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Guided Filing Experience
                </Typography>
                <Typography variant="body1">
                  With our guided filing service, you will go through the steps
                  of filling out your California Form 109 smoothly and with
                  ease.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#ffff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/secure-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/secure-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/secure-icon.png"
                      alt="Unparalleled Security"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Unparalleled Security
                </Typography>
                <Typography variant="body1">
                  Your organization's information is protected with
                  industry-standard encryption and secure technology, ensuring
                  your California Form 109 data remains safe throughout the
                  filing process.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#ffff',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/accuracy-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/accuracy-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/accuracy-icon.png"
                      alt="Built for Accuracy & Compliance"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Built for Accuracy & Compliance
                </Typography>
                <Typography variant="body1">
                  Built-in validations help identify common filing errors and
                  support compliance with California Franchise Tax Board (FTB)
                  filing requirements.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Box
            component="div"
            sx={{
              margin: 'auto',
              textAlign: 'center',
              mt: 4,
              mb: { xs: 0, md: 0 },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 21, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                lineHeight: 1.5,
                mb: 3,
              }}
            >
              From preparing your return to secure electronic filing,{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              TaxZerone helps you complete FTB CA Form 109 with confidence.
            </Typography>
            <Button
              variant="contained"
              sx={{
                transition: 'all 0.3s ease 0s',
                '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
              }}
              className="btn-primary medium"
              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=californiaform109`}
            >
              E-File Now
            </Button>
            <Typography variant="body2" sx={{ mt: 1, mb: { xs: 2, md: 0 } }}>
              Simplified e-filing process
            </Typography>
          </Box>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={{ xs: 5, sm: 5, md: 6 }}
            mb={{ xs: 5, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2 }}
                justifyContent={{ xs: 'center', md: 'center' }}
              >
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-1"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-2"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-3"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-4"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-5"
                />
              </Stack>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: { xs: 1, sm: 3 },
                }}
              >
                Hear What Our Clients Say About Us
              </Typography>

              <Grid
                container
                spacing={2}
                m={0}
                justifyContent="center"
                direction="row"
                className="MuiGrid-root-width"
                sx={{ mb: 3 }}
              >
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#f0f2f9',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography component="div" variant="body1" color="gray">
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: { xs: 16, sm: 18, md: 20 },
                            fontFamily: 'san-serif',
                            fontStyle: 'italic',
                            color: '#414042',
                            lineHeight: 1.2,
                          }}
                        >
                          I had tried other filing options before, but this was
                          the one that actually worked for what I needed. The
                          forms were well organized and the filing process was
                          straightforward. Very happy with the experience.{' '}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'right' }}
                    >
                      - Shawn Garner
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#f0f2f9',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography component="div" variant="body1" color="gray">
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: { xs: 16, sm: 18, md: 20 },
                            fontFamily: 'san-serif',
                            fontStyle: 'italic',
                            color: '#414042',
                            lineHeight: 1.2,
                          }}
                        >
                          I wasn't sure which information needed to go where,
                          but the system guided me through the required fields
                          and helped me complete my California Form 109 with
                          much more confidence.{' '}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'right' }}
                    >
                      - Susan Carlson
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#f0f2f9',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography component="div" variant="body1" color="gray">
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: { xs: 16, sm: 18, md: 20 },
                            fontFamily: 'san-serif',
                            fontStyle: 'italic',
                            color: '#414042',
                            lineHeight: 1.2,
                          }}
                        >
                          The filing process was much smoother than I expected.
                          The system guided me through the related forms,
                          checked for errors, and helped me enter the
                          information in the right places. Before I knew it, my
                          return was ready to file.{' '}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'right' }}
                    >
                      - Edward Pleasant
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Box
        component="section"
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Ready to E-File California Form 109?
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Report your California unrelated business income with confidence.
          TaxZerone simplifies
          <br /> CA Form 109 filing with a secure, guided e-filing experience
          designed for exempt organizations.
        </Typography>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=californiaform109`}
          >
            E-File FTB Form 109
          </Button>
        </Box>
      </Box>
      <Container maxWidth="lg" component="section">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 3 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. How is California Form 109 different from IRS Form 990-T?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <ul style={{ marginBottom: 16 }}>
                      <li style={{ marginBottom: 12 }}>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          CA Form 109
                        </strong>{' '}
                        and{' '}
                        <strong style={{ fontWeight: 600 }}>
                          IRS Form 990-T
                        </strong>{' '}
                        both report unrelated business taxable income (UBTI),
                        but they are filed with different tax authorities.
                      </li>
                      <li style={{ marginBottom: 12 }}>
                        <Link
                          href="/resources/tax-exempt/form-990-t-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Form 990-T
                        </Link>{' '}
                        is the federal return filed with the IRS, while CA Form
                        109 is the state return filed with the California
                        Franchise Tax Board (FTB). Organizations with unrelated
                        business income may need to file both returns if they
                        meet the applicable federal and California filing
                        requirements.
                      </li>
                    </ul>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What are the common mistakes to avoid when filing
                  California Form 109?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <Typography variant="body1" sx={{ mb: 0, mt: 1, ml: 0 }}>
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Common filing mistakes
                      </strong>{' '}
                      include reporting incorrect unrelated business income,
                      overlooking allowable deductions, omitting required
                      supporting forms or schedules, using incorrect California
                      apportionment information for multi-state activities, and
                      submitting incomplete organization details. Reviewing your
                      return before filing can help reduce processing delays and
                      notices from the FTB.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Can I get an extension to file California Form 109?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <Typography variant="body1" sx={{ mb: 0, mt: 1 }}>
                      Yes. Most organizations are automatically granted a
                      six-month extension to file Form 109, provided they are in
                      good standing. However, the extension applies only to the
                      filing of the return, not to the payment of tax. Any tax
                      due must generally be paid by the original due date to
                      avoid penalties and interest.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. What counts as unrelated business income for California
                  Form 109?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <Typography variant="body1" sx={{ mb: 0, mt: 1 }}>
                      Unrelated business income (UBI) is income earned from a
                      trade or business that is regularly carried on and is not
                      substantially related to your organization's exempt
                      purpose. California Form 109 uses this income to determine
                      unrelated business taxable income (UBTI) after allowable
                      deductions, which is used to calculate any applicable
                      California tax.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. How do nonprofits apportion unrelated business income
                  between California and other states?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <Typography variant="body1" sx={{ mb: 0, mt: 1 }}>
                      If a nonprofit conducts unrelated business activities both
                      inside and outside California, it generally cannot report
                      its total unrelated business income as California income.
                      Instead, it must determine the portion attributable to
                      California using the state's apportionment rules and, when
                      applicable, complete Schedule R (Apportionment and
                      Allocation of Unrelated Business Income) to calculate the
                      income taxable by California
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default FormCA109;
